import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { SearchFilters, SearchResultsTable, RecentSearches } from '../components/search';
import { useSearch } from '../hooks/useSearch';
import { setSearchFilters, clearSearch, selectSearchFilters } from '../store/searchSlice';

export default function Search() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector(selectSearchFilters);
  const { results, isSearching, hasSearched, recentSearches, search } = useSearch();

  // The filters/results in Redux are meant to survive a Search -> Viewer ->
  // back round trip, but should NOT survive navigating anywhere else (e.g.
  // to Cash Sale and back) - that would show a stale search the user never
  // asked to keep. Only handleViewTransaction below flips this ref, so on
  // unmount we can tell "went to a viewer" apart from "went elsewhere" and
  // clear the stored search in the latter case.
  const navigatingToViewerRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!navigatingToViewerRef.current) {
        dispatch(clearSearch());
      }
    };
  }, [dispatch]);

  const handleFiltersChange = (newFilters) => {
    dispatch(setSearchFilters(newFilters));
  };

  const handleSearch = (currentFilters) => {
    search(currentFilters);
  };

  const handleRecentSearchSelect = (savedFilters) => {
    dispatch(setSearchFilters(savedFilters));
    search(savedFilters);
  };

  const handleViewTransaction = (transaction) => {
    // Navigate to viewer page based on transaction type
    const routeMap = {
      cash_sale: '/view/cash-sale',
      refund: '/view/refund',
      invoice: '/view/invoice',
      credit_note: '/view/credit-note'
    };

    const route = routeMap[transaction.type];
    if (route) {
      navigatingToViewerRef.current = true;
      navigate(`${route}/${transaction.id}`, { state: { from: 'search' } });
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Search Transactions
      </Typography>

      <RecentSearches searches={recentSearches} onSelect={handleRecentSearchSelect} />

      <SearchFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        isSearching={isSearching}
      />

      {hasSearched && (
        <Box sx={{ mt: 3 }}>
          <SearchResultsTable
            results={results}
            onViewTransaction={handleViewTransaction}
          />
        </Box>
      )}
    </Container>
  );
}